import { Fragment } from 'react';

/**
 * Split a line into React nodes, converting `**word**` segments into
 * <strong> elements and `[text](href)` segments into <a> elements. Used by
 * the Terms and Privacy markdown renderers so inline bold and inline links
 * (e.g. `[Contact form](/contact)`) render correctly.
 *
 * Intentionally tiny — does not handle nested formatting, italics, or escape
 * sequences. The source markdown for those two pages only uses plain
 * `**bold**` and `[text](url)` so a regex split is sufficient and fast.
 *
 * Links beginning with `/` are treated as internal routes; everything else
 * (http/https, mailto:, tel:) opens in a new tab with safe rel attributes.
 */
export function renderInlineMarkdown(text) {
    if (!text) return text;
    // Split on either a **bold** run or a [text](href) link. Keep the
    // separators in the output (capture group) so we can map each part.
    const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (linkMatch) {
            const label = linkMatch[1];
            const href = linkMatch[2];
            const isExternal = !href.startsWith('/');
            return (
                <a
                    key={i}
                    href={href}
                    className="text-zinc-900 underline font-semibold hover:opacity-70"
                    {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                    {label}
                </a>
            );
        }
        return <Fragment key={i}>{part}</Fragment>;
    });
}
